<template>
  <Card
    :btnMsg="$t('showGiftCard.btn_text')"
    @changeStep="changeStep"
    :showBtn="false"
    :prevStepName="'formBasic'"
    :embedded="embedded"
  >
    <div class="card-showGiftCard">
      <div class="card-content">
        <div class="gift-choice-heading">
          <span class="gift-choice-step">1</span>
          <p>{{ $t("showGiftCard.gift_card_text") }}</p>
        </div>
        <div v-if="channelLoading" class="channel-loading">
          <van-loading size="24px" color="#0ea5e9" />
        </div>
        <div v-else-if="!channelList.length" class="channel-empty">
          {{ $t("networkErrorText") }}
        </div>
        <div v-else class="gift-card-carousel-wrap">
          <button
            v-if="channelList.length > 1"
            type="button"
            class="gift-carousel-arrow gift-carousel-prev"
            aria-label="Previous gift card"
            @click="scrollGifts(-1)"
          >
            ‹
          </button>
          <div
            ref="giftCarousel"
            class="reference-gift-grid gift-card-carousel"
            :class="{ 'single-gift': channelList.length === 1 }"
            @scroll="updateGiftIndex"
          >
            <button
              v-for="(item, index) in channelList"
              :key="giftKey(item, index)"
              type="button"
              class="channel-gift-option"
              @click="selectGift(item, index)"
            >
              <img :src="item.channel_pic" :alt="giftName(item)" />
            </button>
          </div>
          <div v-if="channelList.length > 1" class="gift-carousel-dots">
            <button
              v-for="(item, index) in channelList"
              :key="giftKey(item, index)"
              type="button"
              :class="{ active: currentIndex === index }"
              :aria-label="`Show gift card ${index + 1}`"
              @click="showGift(index)"
            ></button>
          </div>
          <button
            v-if="channelList.length > 1"
            type="button"
            class="gift-carousel-arrow gift-carousel-next"
            aria-label="Next gift card"
            @click="scrollGifts(1)"
          >
            ›
          </button>
        </div>
        <div class="reference-claim-action">
          <van-button
            class="reference-primary"
            :disabled="
              channelLoading || !channelList.length || !selectedGiftKey
            "
            @click="changeStep"
            >{{ $t("showGiftCard.btn_text") }}</van-button
          >
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import Card from "@/components/card.vue";
import { postChannelList } from "@/api/portal";

export default {
  components: {
    Card,
  },
  props: {
    embedded: { type: Boolean, default: false },
    value: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      form: {},
      currentIndex: 0,
      channelList: [],
      channelLoading: false,
      selectedGiftKey: null,
      selectedGift: null,
    };
  },
  async mounted() {
    this.form = this.value;
    this.selectedGift = this.$parent.selectedGift || null;
    await this.getChannelList();
    if (this.selectedGift) {
      const selectedIndex = this.channelList.findIndex(
        (item) => this.giftKey(item) === this.giftKey(this.selectedGift),
      );
      if (selectedIndex >= 0)
        this.selectedGiftKey = this.giftKey(
          this.channelList[selectedIndex],
          selectedIndex,
        );
      if (selectedIndex >= 0) {
        this.currentIndex = selectedIndex;
        this.$nextTick(() => {
          const carousel = this.$refs.giftCarousel;
          const card = carousel && carousel.children[selectedIndex];
          if (card) card.scrollIntoView({ block: "nearest", inline: "start" });
        });
      }
    }
    if (!this.selectedGiftKey && this.channelList.length)
      this.selectGift(this.channelList[0], 0);
  },
  methods: {
    async getChannelList() {
      this.channelLoading = true;
      try {
        const res = await postChannelList({
          order_id: this.form.order_id,
          email: this.form.email,
        });
        this.channelList = Array.isArray(res.data) ? res.data : [];
      } catch (error) {
        this.channelList = [];
      } finally {
        this.channelLoading = false;
      }
    },
    giftKey(item, index) {
      return String(item.id || item.channel_id || index || 0);
    },
    giftName(item) {
      if (!item) return "";
      const localizedName =
        this.$i18n.locale === "zh" ? item.channel_name_zh : item.channel_name;
      return localizedName || item.channel_name || item.channel_name_zh || "";
    },
    selectGift(item, index) {
      this.currentIndex = index;
      this.selectedGiftKey = this.giftKey(item, index);
      this.selectedGift = item;
      this.$parent.selectedGift = item;
    },
    changeStep() {
      if (!this.selectedGiftKey) {
        this.$toast.fail(this.$t("chooseGiftFirst"));
        return;
      }
      this.form.channel_id = this.selectedGift.id;
      this.form.channel_type = this.selectedGift.channel_type;
      this.form.channel_pic = this.selectedGift.channel_pic;
      this.$parent.changeStep("uploadPic", this.form);
    },
    scrollGifts(direction) {
      const nextIndex =
        (this.currentIndex + direction + this.channelList.length) %
        this.channelList.length;
      this.showGift(nextIndex);
    },
    showGift(index) {
      const carousel = this.$refs.giftCarousel;
      const card = carousel && carousel.children[index];
      if (card)
        card.scrollIntoView({
          block: "nearest",
          inline: "start",
          behavior: "smooth",
        });
    },
    updateGiftIndex(event) {
      this.currentIndex = Math.round(
        event.target.scrollLeft / event.target.clientWidth,
      );
      const item = this.channelList[this.currentIndex];
      if (item) this.selectGift(item, this.currentIndex);
    },
  },
};
</script>

<style lang="scss">
.card-wrapper--stage-two
  .reference-stage-two-heading
  .reference-step-heading
  p.card-title {
  font-size: 12px !important;
}

@media (min-width: 641px) {
  .card-wrapper--stage-two
    .reference-stage-two-heading
    .reference-step-heading
    p.card-title {
    font-size: 18px !important;
  }

  .card-showGiftCard .card-content .card-title {
    font-size: 18px !important;
  }
}

.card-showGiftCard {
  position: relative;
  width: 100%;
  .gift-card-carousel-wrap {
    position: relative;
    margin-top: 16px;
  }
  .gift-carousel-arrow {
    position: absolute;
    top: 50%;
    z-index: 2;
    display: grid;
    width: 32px;
    height: 32px;
    place-items: center;
    transform: translateY(-50%);
    border: 1px solid #e2e8f0;
    border-radius: 50%;
    background: #64748b;
    color: #fff;
    font-size: 24px;
    line-height: 1;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.16);
    cursor: pointer;
    &:disabled {
      opacity: 0.4;
      cursor: default;
    }
  }
  .gift-carousel-prev {
    left: 4px;
  }
  .gift-carousel-next {
    right: 4px;
  }
  .gift-carousel-dots {
    position: absolute;
    bottom: 16px;
    left: 50%;
    z-index: 2;
    display: flex;
    justify-content: center;
    gap: 7px;
    transform: translateX(-50%);

    button {
      width: 8px;
      height: 8px;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: #cbd5e1;
      cursor: pointer;

      &.active {
        background: #64748b;
      }
    }
  }
  .gift-card-carousel {
    display: flex;
    justify-content: flex-start;
    gap: 14px;
    overflow-x: auto;
    padding-bottom: 8px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &.single-gift {
      justify-content: center;
    }

    &::-webkit-scrollbar {
      display: none;
    }

    .channel-gift-option {
      flex: 0 0 100%;
      max-width: 100%;
      scroll-snap-align: center;
    }
  }
  .channel-gift-option {
    position: relative;
    display: flex;
    width: fit-content;
    max-width: 100%;
    max-height: 220px;
    align-items: center;
    justify-content: center;
    justify-self: center;
    box-sizing: border-box;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 12px;
    background: transparent;
    cursor: pointer;
    transition: box-shadow 0.25s ease, border-color 0.25s ease,
      transform 0.25s ease;
    &:hover {
      transform: translateY(-2px);
    }
    img {
      display: block;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 220px;
      border-radius: 10px;
      object-fit: contain;
    }
  }
  .card-title {
    font-size: 12px !important;
    font-weight: 500;
    color: #1a1a1a;
    margin-bottom: 20px;
    white-space: nowrap;
  }
  .card-text {
    font-size: 16px;
    font-weight: 400;
    color: #1a1a1a;
    margin-bottom: 20px;
  }
  .gift-card-box {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: linear-gradient(180deg, #ffd814aa, #ffa031aa);
    border-radius: 10px;
    border: 2px solid #ffd814aa;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    .gift-card-box-pic {
      img {
        width: 100%;
      }
    }
    .gift-card-box-text {
      font-size: 21px;
      font-weight: 500;
      text-align: center;
    }
  }
  .card-swipe {
    position: relative;
    border-radius: 8px;
    .van-swipe {
      border-radius: 8px;
    }
  }
  .van-swipe__indicators {
    bottom: 20px;
  }
  .swipe-arrow {
    width: 28px;
    border: none;
    height: 28px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: #000;
    font-size: 23px;
    background-color: #ffd814;
    padding: 0;
    box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    border-radius: 2px;
  }

  .prev-arrow {
    left: 6px;
  }

  .next-arrow {
    right: 6px;
  }
  .van-swipe__indicator .van-swipe__indicator-item {
    background-color: #fff;
  }

  .van-swipe__indicator .van-swipe__indicator-item--active {
    background-color: #f4a300; /* 高亮颜色 */
  }
  .van-swipe__track {
    display: flex;
    align-items: center;
  }
  .van-swipe {
    border: 1px solid #f4a300;
    // background: linear-gradient(180deg, rgba(245, 200, 0, 0.6666666667), #ffa031);
    background: #fff;
  }
}
</style>
